import {
  Box,
  Button,
  Flex,
  FormLabel,
  Input,
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  Checkbox,
  CheckboxGroup,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { CommercialConsultantService } from '../../../services/CommercialConsultant/CommercialConsultantService'
import { CommercialConsultant, CommercialConsultantInput, WorkTime } from '../../../services/CommercialConsultant/types'
import { daysOfWeek } from './constants'


interface ICommercialConsultantFormModalProps {
  isOpen: boolean
  onClose: () => void
  consultant: CommercialConsultant | null
}

export default function CommercialConsultantFormModal({
  isOpen,
  onClose,
  consultant
}: ICommercialConsultantFormModalProps) {
  const service = new CommercialConsultantService()
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<CommercialConsultantInput>({
    name: '',
    email: '',
    work_times: [],
    is_online_recorded_course: false,
    is_online_live_course: false
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [workTimeErrors, setWorkTimeErrors] = useState<string[]>(Array(7).fill(''))

  useEffect(() => {
    if (consultant) {
      const workTimes: WorkTime[] = []
      consultant.work_times.forEach((workTime) => {
        workTimes[(workTime.day_of_week)-1] = {
          day_of_week: +workTime.day_of_week,
          start_time: workTime.start_time || '',
          end_time: workTime.end_time || ''
        }
      })
      setForm({
        name: consultant.name,
        email: consultant.email,
        work_times: workTimes,
        is_online_recorded_course: consultant.is_online_recorded_course || false,
        is_online_live_course: consultant.is_online_live_course || false
      })
      setWorkTimeErrors(Array(7).fill(''))
    } else {
      setForm({
        name: '',
        email: '',
        work_times: [],
        is_online_recorded_course: false,
        is_online_live_course: false
      })
      setWorkTimeErrors(Array(7).fill(''))
    }
    setFormErrors({})
  }, [consultant, isOpen])

  // Função para validar horários
  const validateWorkTimes = (work_times: WorkTime[]) => {
    const errors = Array(7).fill('')
    for (let i = 0; i < 7; i++) {
      const wt = work_times[i]
      if (wt) {
        const { start_time, end_time } = wt
        // Regra 2: Se um está preenchido, o outro deve estar também
        if ((start_time && !end_time) || (!start_time && end_time)) {
          errors[i] = 'Preencha os dois horários.'
        } else if (start_time && end_time) {
          // Regra 1: início < fim
          if (start_time >= end_time) {
            errors[i] = 'O início deve ser menor que o fim.'
          }
        }
      }
    }
    setWorkTimeErrors(errors)
    return errors
  }

  // Validação automática ao alterar horários
  useEffect(() => {
    validateWorkTimes(form.work_times)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.work_times])

  const handleSave = async () => {
    setSaving(true)
    try {
      const formData = {
        ...form,
        work_times: form.work_times.filter((w) => w?.start_time && w?.end_time)
      }
      // Regra 3: Não permitir submit se houver erro
      const errors = validateWorkTimes(form.work_times)
      if (errors.some((e) => e)) {
        setSaving(false)
        return
      }
      if (consultant) {
        await service.update(consultant.id, formData)
        toast({ title: 'Consultor atualizado com sucesso', status: 'success', duration: 3000, isClosable: true })
      } else {
        await service.create(formData)
        toast({ title: 'Consultor cadastrado com sucesso', status: 'success', duration: 3000, isClosable: true })
      }
      onClose()
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = err as any
      if (error.response?.status === 422) setFormErrors(error.response.data.errors)
      else toast({ title: 'Erro ao salvar consultor', status: 'error', duration: 3000, isClosable: true })
    } finally {
      setSaving(false)
    }
  }

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{consultant ? 'Editar' : 'Cadastrar'} Consultor Comercial</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={3}>
            <Box>
              <FormLabel>Nome</FormLabel>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={saving} />
              {formErrors.name && <Text color="red.500">{formErrors.name}</Text>}
            </Box>
            <Box>
              <FormLabel>E-mail</FormLabel>
              <Input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={saving}
              />
              {formErrors.email && <Text color="red.500">{formErrors.email}</Text>}
            </Box>
            <Box>
              <FormLabel>Tipos de Curso Atendidos</FormLabel>
              <CheckboxGroup>
                <Flex gap={4} mb={2}>
                  <Checkbox
                    colorScheme="magenta"
                    isChecked={form.is_online_live_course}
                    onChange={(e) => setForm({ ...form, is_online_live_course: e.target.checked })}
                    isDisabled={saving}
                  >
                    Presencial e Online (ao vivo)
                  </Checkbox>
                  <Checkbox
                    colorScheme="magenta"
                    isChecked={form.is_online_recorded_course}
                    onChange={(e) => setForm({ ...form, is_online_recorded_course: e.target.checked })}
                    isDisabled={saving}
                  >
                    Online (gravado)
                  </Checkbox>
                </Flex>
              </CheckboxGroup>
            </Box>
            <Box>
              <FormLabel>Horários de Trabalho</FormLabel>
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th textAlign="left" p={1}>
                        Dia
                      </Th>
                      <Th textAlign="center" p={1}>
                        Início
                      </Th>
                      <Th textAlign="center" p={1}>
                        Fim
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {daysOfWeek.map((day, idx) => (
                      <>
                        <Tr key={day}>
                          <Td p={1}>{day}</Td>
                          <Td p={1} textAlign="center">
                            <Input
                              type="time"
                              size="sm"
                              value={form.work_times[idx]?.start_time || ''}
                              onChange={(e) => {
                                const newWorkTimes = [...form.work_times]
                                if (!newWorkTimes[idx])
                                  newWorkTimes[idx] = { day_of_week: idx + 1, start_time: '', end_time: '' }
                                newWorkTimes[idx].start_time = e.target.value
                                setForm({ ...form, work_times: newWorkTimes })
                              }}
                              disabled={saving}
                              w="100%"
                            />
                          </Td>
                          <Td p={1} textAlign="center">
                            <Input
                              type="time"
                              size="sm"
                              value={form.work_times[idx]?.end_time || ''}
                              onChange={(e) => {
                                const newWorkTimes = [...form.work_times]
                                if (!newWorkTimes[idx])
                                  newWorkTimes[idx] = { day_of_week: idx + 1, start_time: '', end_time: '' }
                                newWorkTimes[idx].end_time = e.target.value
                                setForm({ ...form, work_times: newWorkTimes })
                              }}
                              disabled={saving}
                              w="100%"
                            />
                          </Td>
                        </Tr>
                        {workTimeErrors[idx] && (
                          <Tr key={day + '-err'}>
                            <Td
                              colSpan={3}
                              p={1}
                              style={{ color: 'red', fontSize: 13, paddingLeft: 8, paddingBottom: 4 }}
                            >
                              {workTimeErrors[idx]}
                            </Td>
                          </Tr>
                        )}
                      </>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              {formErrors.work_times && <Text color="red.500">{formErrors.work_times}</Text>}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="magenta" mr={3} onClick={handleSave} isLoading={saving}>
            Salvar
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
        </ModalFooter>
      </ModalContent>
    </ChakraModal>
  )
}
